/**
 * EEOC Test Results Page
 * Displays detailed compliance evaluation results
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Shield, CheckCircle, AlertTriangle, XCircle, Award,
  ArrowLeft, Download, BookOpen, RefreshCcw, Target,
  FileText, Lightbulb, AlertOctagon, BookMarked, Loader2
} from 'lucide-react';
import { cn } from '@/lib/utils';

export default function EEOCResults() {
  const { linkId } = useParams();
  const navigate = useNavigate();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchResults();
  }, [linkId]);

  const fetchResults = async () => {
    try {
      const response = await api.get(`/eeoc/test/results/${linkId}`);
      setResults(response.data);
    } catch (err) {
      if (err.response?.status === 400) {
        toast.error('Test not completed yet');
        navigate(`/eeoc/test/${linkId}`);
      } else {
        toast.error('Failed to load results');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusInfo = (status) => {
    const info = {
      qualified: {
        icon: Award,
        color: 'text-emerald-600 dark:text-emerald-400',
        bg: 'bg-emerald-100 dark:bg-emerald-900/30',
        border: 'border-emerald-500',
        label: 'Qualified',
        message: 'Excellent! You demonstrated strong EEOC compliance knowledge.'
      },
      needs_improvement: {
        icon: AlertTriangle,
        color: 'text-amber-600 dark:text-amber-400',
        bg: 'bg-amber-100 dark:bg-amber-900/30',
        border: 'border-amber-500',
        label: 'Needs Training',
        message: 'Some areas need improvement. Review the guidance below.'
      },
      not_qualified: {
        icon: XCircle,
        color: 'text-red-600 dark:text-red-400',
        bg: 'bg-red-100 dark:bg-red-900/30',
        border: 'border-red-500',
        label: 'Not Qualified',
        message: 'Additional training required. Please review EEOC guidelines.'
      }
    };
    return info[status] || info.needs_improvement;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-500" />
      </div>
    );
  }

  if (!results) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
              Results Not Available
            </h2>
            <p className="text-slate-600 dark:text-slate-400">
              Please complete the assessment first.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusInfo = getStatusInfo(results.qualification_status);
  const StatusIcon = statusInfo.icon;
  const evaluation = results.evaluation || {};
  const scoreBreakdown = evaluation.score_breakdown || {};

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="eeoc-results">
      {/* Header */}
      <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate(`/eeoc/test/${linkId}`)}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="w-10 h-10 bg-slate-900 dark:bg-slate-100 rounded-md flex items-center justify-center">
                <Shield className="w-5 h-5 text-white dark:text-slate-900" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-slate-900 dark:text-white">Assessment Results</h1>
                <p className="text-xs text-slate-500 dark:text-slate-400">{results.user_name}</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {/* Score Overview Card */}
        <Card className={cn("border-2", statusInfo.border)}>
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row items-center gap-6">
              {/* Score Circle */}
              <div className="flex-shrink-0">
                <div className={cn("w-32 h-32 rounded-full flex flex-col items-center justify-center", statusInfo.bg)}>
                  <span className={cn("text-4xl font-bold", statusInfo.color)}>{results.score || 0}%</span>
                  <span className="text-xs text-slate-500 dark:text-slate-400">Overall Score</span>
                </div>
              </div>
              
              {/* Status Info */}
              <div className="flex-1 text-center md:text-left">
                <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
                  <StatusIcon className={cn("h-6 w-6", statusInfo.color)} />
                  <Badge className={cn(statusInfo.bg, statusInfo.color, "text-base px-3 py-1")}>
                    {statusInfo.label}
                  </Badge>
                </div>
                <p className="text-slate-600 dark:text-slate-400">{statusInfo.message}</p>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
                  Attempt #{results.attempt_count} • Completed on {new Date(results.completed_at).toLocaleDateString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Score Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Target className="h-5 w-5" />
              Score Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { key: 'safe_questioning', label: 'Safe Questioning', description: 'Asked only job-related questions' },
                { key: 'boundary_awareness', label: 'Boundary Awareness', description: 'Avoided EEOC-risky topics' },
                { key: 'neutral_language', label: 'Neutral Language', description: 'Used inclusive language' },
                { key: 'proper_redirection', label: 'Proper Redirection', description: 'Handled protected info appropriately' },
              ].map(item => (
                <div key={item.key} className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium text-slate-900 dark:text-white">{item.label}</span>
                    <span className="text-lg font-bold text-slate-900 dark:text-white">
                      {scoreBreakdown[item.key] || 0}/25
                    </span>
                  </div>
                  <Progress value={((scoreBreakdown[item.key] || 0) / 25) * 100} className="h-2" />
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">{item.description}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Detailed Feedback Tabs */}
        <Tabs defaultValue="risks" className="space-y-4">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="risks" className="text-xs">
              <AlertOctagon className="h-4 w-4 mr-1" />
              Risks
            </TabsTrigger>
            <TabsTrigger value="redirections" className="text-xs">
              <RefreshCcw className="h-4 w-4 mr-1" />
              Redirections
            </TabsTrigger>
            <TabsTrigger value="legal" className="text-xs">
              <BookMarked className="h-4 w-4 mr-1" />
              Legal
            </TabsTrigger>
            <TabsTrigger value="improvement" className="text-xs">
              <Lightbulb className="h-4 w-4 mr-1" />
              Plan
            </TabsTrigger>
          </TabsList>

          {/* Risk Statements Tab */}
          <TabsContent value="risks">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Risk Statements Identified</CardTitle>
                <CardDescription>Statements that could pose EEOC compliance risks</CardDescription>
              </CardHeader>
              <CardContent>
                {(evaluation.risk_statements || []).length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
                    <p className="text-slate-600 dark:text-slate-400">
                      No significant risk statements identified. Great job!
                    </p>
                  </div>
                ) : (
                  <ScrollArea className="h-[300px]">
                    <div className="space-y-4">
                      {evaluation.risk_statements.map((risk, idx) => (
                        <div key={idx} className="p-4 border border-slate-200 dark:border-slate-700 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant={
                              risk.risk_level === 'high' ? 'destructive' :
                              risk.risk_level === 'medium' ? 'default' : 'secondary'
                            }>
                              {risk.risk_level?.toUpperCase()}
                            </Badge>
                            <Badge variant="outline">{risk.category}</Badge>
                          </div>
                          <p className="text-sm font-medium text-slate-900 dark:text-white mb-2">
                            "{risk.statement}"
                          </p>
                          <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                            {risk.explanation}
                          </p>
                          <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded border border-emerald-200 dark:border-emerald-800">
                            <p className="text-sm text-emerald-700 dark:text-emerald-300">
                              <strong>Better approach:</strong> {risk.better_alternative}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Missed Redirections Tab */}
          <TabsContent value="redirections">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Missed Redirection Opportunities</CardTitle>
                <CardDescription>Moments where you could have better redirected the conversation</CardDescription>
              </CardHeader>
              <CardContent>
                {(evaluation.missed_redirections || []).length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
                    <p className="text-slate-600 dark:text-slate-400">
                      You handled all redirections well!
                    </p>
                  </div>
                ) : (
                  <ScrollArea className="h-[300px]">
                    <div className="space-y-4">
                      {evaluation.missed_redirections.map((item, idx) => (
                        <div key={idx} className="p-4 border border-slate-200 dark:border-slate-700 rounded-lg">
                          <div className="mb-3">
                            <p className="text-xs text-slate-500 dark:text-slate-400 uppercase mb-1">Candidate said:</p>
                            <p className="text-sm text-slate-900 dark:text-white">"{item.candidate_statement}"</p>
                          </div>
                          <div className="mb-3">
                            <p className="text-xs text-slate-500 dark:text-slate-400 uppercase mb-1">You responded:</p>
                            <p className="text-sm text-amber-700 dark:text-amber-300">"{item.recruiter_response}"</p>
                          </div>
                          <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded border border-emerald-200 dark:border-emerald-800">
                            <p className="text-xs text-slate-500 dark:text-slate-400 uppercase mb-1">Recommended response:</p>
                            <p className="text-sm text-emerald-700 dark:text-emerald-300">{item.proper_response}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Legal Explanation Tab */}
          <TabsContent value="legal">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Legal Context</CardTitle>
                <CardDescription>Understanding the EEOC implications of your responses</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="prose prose-slate dark:prose-invert max-w-none">
                  <p className="text-slate-600 dark:text-slate-400 whitespace-pre-line">
                    {evaluation.legal_explanation || 'No specific legal concerns noted. Your interview questions were generally compliant with EEOC guidelines.'}
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Improvement Plan Tab */}
          <TabsContent value="improvement">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Improvement Plan</CardTitle>
                <CardDescription>Actionable steps to improve your EEOC compliance</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {(evaluation.improvement_plan || []).length > 0 && (
                    <div>
                      <h4 className="font-medium text-slate-900 dark:text-white mb-3">Recommended Actions:</h4>
                      <ul className="space-y-2">
                        {evaluation.improvement_plan.map((item, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                            <span className="text-slate-600 dark:text-slate-400">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {(evaluation.retraining_tasks || []).length > 0 && (
                    <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-700">
                      <h4 className="font-medium text-amber-700 dark:text-amber-300 mb-3 flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5" />
                        Required Retraining:
                      </h4>
                      <ul className="space-y-2">
                        {evaluation.retraining_tasks.map((task, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <span className="w-6 h-6 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold text-amber-700 dark:text-amber-300">
                              {idx + 1}
                            </span>
                            <span className="text-slate-600 dark:text-slate-400">{task}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            variant="outline"
            onClick={() => navigate(`/eeoc/test/${linkId}/guide`)}
          >
            <BookOpen className="h-4 w-4 mr-2" />
            Review EEOC Guide
          </Button>
          <Button
            onClick={() => navigate(`/eeoc/test/${linkId}`)}
            className="bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:hover:bg-slate-200 dark:text-slate-900"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Overview
          </Button>
        </div>
      </main>
    </div>
  );
}
